import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { fetchUserJobs } from "../../services/jobsService";
import CollapsibleJobRow from "./CollapsibleJobRow";
import { useVoicePreferences } from "../../auth/VoicePreferencesContext";
import {
  TotalJobsIcon,
  CompletedJobsIcon,
  ProcessingJobsIcon,
  DownloadJobsIcon,
  FailedJobsIcon,
  RefreshIcon,
  FunnelIcon,
  ChevronDownSmallIcon,
} from "../../icons";
import {
  STATUS_META,
  FILTER_OPTIONS,
  COMPLETED_STATUSES,
  SERVICE_FILTER_OPTIONS,
} from "../../constants";
import "./JobsDashboard.scss";

// SVG icons imported from src/icons — no inline icon definitions here
function StatCard({ icon, label, value, colorKey }) {
  return (
    <div className={`jd-stat-card jd-stat-card--${colorKey}`}>
      <div className={`jd-stat-icon-wrap jd-stat-icon-wrap--${colorKey}`}>{icon}</div>
      <div className="jd-stat-body">
        <span className="jd-stat-label">{label}</span>
        <span className="jd-stat-value">{value}</span>
      </div>
    </div>
  );
}

const _getStatusMeta = (status = "") =>
  STATUS_META[status.toUpperCase()] || {
    label: status,
    color: "#64748b",
    bg: "#f8fafc",
    dot: "#94a3b8",
  };
void _getStatusMeta;

// Returns a human-readable label for a filter value
const getFilterLabel = (filterValue) => {
  if (filterValue === "ALL") return "All Statuses";
  const meta = STATUS_META[filterValue.toUpperCase()];
  return meta ? meta.label : filterValue.charAt(0) + filterValue.slice(1).toLowerCase();
};

const gf = (obj, ...keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
  }
  return null;
};

const allLangsVideoTranslated = (job) => {
  const langs = Array.isArray(job.language_statuses) ? job.language_statuses : [];
  return (
    langs.length > 0 &&
    langs.every(
      (ls) =>
        (gf(ls, "sync_status", "SyncStatus", "status", "Status") || "").toUpperCase() ===
        "VIDEO_TRANSLATED"
    )
  );
};

function JobsDashboard({ userEmail }) {
  const { fetchPreferences } = useVoicePreferences();
  const navigate = useNavigate();
  const auth = useAuth();

  // Read token directly from live auth context — avoids localStorage key mismatch.
  // Prefer id_token (Cognito User Pools Authorizer validates this);
  // fall back to access_token for JWT Authorizer setups.
  const authToken = auth?.user?.id_token || auth?.user?.access_token || null;

  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [serviceFilter, setServiceFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  // Track which job rows have had actions taken (for optimistic UI)
  const [jobActionOverrides, setJobActionOverrides] = useState({});

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) return;
    const email = auth.user?.profile?.email || auth.user?.profile?.sub || "";
    fetchPreferences(email);
  }, [auth.isAuthenticated, auth.user, fetchPreferences]);

  // Defined first so handleActionRefresh (below) can safely reference it.
  const loadJobs = useCallback(
    async (isRefresh = false) => {
      if (!userEmail) return; // Guard: skip fetch if userEmail is not yet available
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      try {
        const data = await fetchUserJobs(userEmail, undefined, authToken);
        setJobs(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[JobsDashboard] Failed to fetch jobs:", err);
        setError(err.message || "Failed to load jobs. Please try again.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [userEmail, authToken]
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Called by child rows after sync/download to refresh the table.
  // Defined after loadJobs so the dependency is already in scope.
  const handleActionRefresh = useCallback(() => {
    // Small delay so the backend has time to persist the new status
    setTimeout(() => loadJobs(true), 1500);
  }, [loadJobs]);

  // Optimistically update a single job's processing_status in local state
  // so the Action button reacts immediately without waiting for an API round-trip
  const handleOptimisticStatus = useCallback((jobId, newStatus) => {
    setJobActionOverrides((prev) => ({ ...prev, [jobId]: newStatus }));
  }, []);

  const stats = {
    total: jobs.length,
    completed: jobs.filter((j) =>
      ["DOWNLOADED", "DELETED"].includes((j.processing_status || "").toUpperCase())
    ).length,
    // Still processing = AV_SYNC_INITIATED where NOT all langs are VIDEO_TRANSLATED, or UPLOADED
    processing: jobs.filter((j) => {
      const ps = (j.processing_status || "").toUpperCase();
      if (ps === "UPLOADED") return true;
      if (ps === "AV_SYNC_INITIATED") return !allLangsVideoTranslated(j);
      return false;
    }).length,
    // Ready to download = COMPLETED  OR  AV_SYNC_INITIATED where all langs are VIDEO_TRANSLATED
    readyToDownload: jobs.filter((j) => {
      const ps = (j.processing_status || "").toUpperCase();
      if (ps === "COMPLETED") return true;
      if (ps === "AV_SYNC_INITIATED") return allLangsVideoTranslated(j);
      return false;
    }).length,
    failed: jobs.filter((j) => (j.processing_status || "").toUpperCase() === "FAILED").length,
  };

  const filteredJobs = jobs.filter((j) => {
    // Status filter
    const ps = (j.processing_status || "").toUpperCase();
    const statusMatch =
      filter === "ALL"
        ? true
        : filter === "COMPLETED"
          ? COMPLETED_STATUSES.includes(ps)
          : ps === filter;

    // Service filter — "Positioning" bucket covers both positioning & professional
    const st = (j.service_type || "").trim().toLowerCase();
    const serviceMatch =
      serviceFilter === "ALL"
        ? true
        : serviceFilter === "Positioning"
          ? st === "positioning" || st === "professional"
          : st === serviceFilter.toLowerCase();

    return statusMatch && serviceMatch;
  });

  if (loading) {
    return (
      <div className="jd-loading">
        <div className="jd-spinner" />
        <p>Loading your jobs…</p>
      </div>
    );
  }

  // so the header with Refresh + Create New Job is always visible

  return (
    <div className="jd-container">
      {/* ── Header Row ── */}
      <div className="jd-header">
        <div className="jd-header-left">
          <h1 className="jd-title">Dashboard</h1>
          <p className="jd-subtitle">Track all your video translation and synchronization</p>
        </div>
        <div className="jd-header-actions">
          {/* Refresh */}
          <button
            className={`jd-refresh-btn${refreshing ? " jd-refresh-btn--spinning" : ""}`}
            onClick={() => loadJobs(true)}
            disabled={refreshing}
            title="Refresh"
          >
            <RefreshIcon spinning={refreshing} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>

          {/* Status filter pill */}
          <div className={`jd-filter-wrap${filter !== "ALL" ? " jd-filter-wrap--active" : ""}`}>
            <FunnelIcon />
            <span className="jd-filter-text">
              <span className="jd-filter-label">Status</span>
              <span className="jd-filter-value">{getFilterLabel(filter)}</span>
            </span>
            <select
              className="jd-filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Filter by status"
            >
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "ALL"
                    ? "All Statuses"
                    : opt === "AV_SYNC_INITIATED"
                      ? "Processing"
                      : opt === "COMPLETED"
                        ? "Completed"
                        : opt.charAt(0) + opt.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
            <ChevronDownSmallIcon />
          </div>

          {/* Service filter pill */}
          <div
            className={`jd-filter-wrap${serviceFilter !== "ALL" ? " jd-filter-wrap--active" : ""}`}
          >
            <FunnelIcon />
            <span className="jd-filter-text">
              <span className="jd-filter-label">Service</span>
              <span className="jd-filter-value">
                {SERVICE_FILTER_OPTIONS.find((o) => o.value === serviceFilter)?.label ||
                  "All Services"}
              </span>
            </span>
            <select
              className="jd-filter-select"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              aria-label="Filter by service"
            >
              {SERVICE_FILTER_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <ChevronDownSmallIcon />
          </div>

          {/* Create New Job */}
          <button className="jd-create-btn" onClick={() => navigate("/services")}>
            + Create New Task
          </button>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      {!loading && (
        <div className="jd-stat-cards">
          <StatCard icon={<TotalJobsIcon />} label="Total" value={stats.total} colorKey="blue" />
          <StatCard
            icon={<CompletedJobsIcon />}
            label="Completed"
            value={stats.completed}
            colorKey="green"
          />
          <StatCard
            icon={<ProcessingJobsIcon />}
            label="Processing"
            value={stats.processing}
            colorKey="amber"
          />
          <StatCard
            icon={<DownloadJobsIcon />}
            label="Ready to Download"
            value={stats.readyToDownload}
            colorKey="teal"
          />
          <StatCard icon={<FailedJobsIcon />} label="Failed" value={stats.failed} colorKey="red" />
        </div>
      )}

      {/* ── Error state (inline — header buttons stay visible) ── */}
      {error && (
        <div className="jd-error-state">
          <div className="jd-error-icon">⚠️</div>
          <h3 className="jd-error-title">Unable to load jobs</h3>
          <p className="jd-error-msg">{error}</p>
          <button className="jd-retry-btn" onClick={() => loadJobs()}>
            🔄 Retry
          </button>
        </div>
      )}

      {/* ── Empty state (first-time user or no jobs) ── */}
      {!error && jobs.length === 0 && (
        <div className="jd-empty-state">
          <div className="jd-empty-icon">📂</div>
          <h3 className="jd-empty-title">No jobs yet</h3>
          <p className="jd-empty-msg">
            You haven&apos;t created any AV Sync jobs yet. Click <strong>Create New Job</strong> to
            get started.
          </p>
        </div>
      )}

      {/* ── Filtered empty state ── */}
      {!error && jobs.length > 0 && filteredJobs.length === 0 && (
        <div className="jd-empty-state jd-empty-state--filter">
          <div className="jd-empty-icon">🔍</div>
          <h3 className="jd-empty-title">No jobs match the selected filters</h3>
          <p className="jd-empty-msg">
            {filter !== "ALL" && serviceFilter !== "ALL" ? (
              <>
                No jobs with status <strong>{getFilterLabel(filter)}</strong> and service{" "}
                <strong>
                  {serviceFilter.charAt(0).toUpperCase() + serviceFilter.slice(1).toLowerCase()}
                </strong>{" "}
                were found.
              </>
            ) : filter !== "ALL" ? (
              <>
                No jobs with status <strong>{getFilterLabel(filter)}</strong> were found.
              </>
            ) : (
              <>
                No jobs for service{" "}
                <strong>
                  {serviceFilter === "Positioning" ? "Positioning / Professional" : serviceFilter}
                </strong>{" "}
                were found.
              </>
            )}
          </p>
          <button
            className="jd-clear-filter-btn"
            onClick={() => {
              setFilter("ALL");
              setServiceFilter("ALL");
            }}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* ── Jobs Table ── */}
      {!error && filteredJobs.length > 0 && (
        <div className="jd-table-wrap">
          <table className="jd-table">
            <thead>
              <tr>
                <th className="jd-th">File Name</th>
                <th className="jd-th">Status</th>
                <th className="jd-th">Service</th>
                <th className="jd-th">Created</th>
                <th className="jd-th">Updated</th>
                <th className="jd-th jd-th--action">Action</th>
                <th className="jd-th jd-th--chevron" />
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job, idx) => {
                const rowKey = job.job_id || idx;
                // Merge optimistic status override if present
                const effectiveJob = jobActionOverrides[job.job_id]
                  ? {
                      ...job,
                      processing_status: jobActionOverrides[job.job_id],
                    }
                  : job;
                return (
                  <CollapsibleJobRow
                    key={rowKey}
                    job={effectiveJob}
                    isEven={idx % 2 !== 0} // even-indexed rows (0-based) get the tinted bg
                    isExpanded={expandedRowId === rowKey}
                    onToggle={() => setExpandedRowId(expandedRowId === rowKey ? null : rowKey)}
                    userEmail={userEmail}
                    onRefresh={handleActionRefresh}
                    onOptimisticStatus={handleOptimisticStatus}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

StatCard.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string,
  value: PropTypes.number,
  colorKey: PropTypes.string,
};

JobsDashboard.propTypes = {
  userEmail: PropTypes.string,
};

export default JobsDashboard;
